import { Capacitor } from '@capacitor/core';

/**
 * FCM 푸시 스텁. Phase δ.4에서 캠페인 콘텐츠 + 핸들러 추가.
 * 현재는 권한 요청 + 토큰 획득까지만 — α.6 검증 게이트에서 콘솔로 테스트
 * 푸시 발송 가능한 상태가 목표.
 *
 * 첫 권한 요청은 사용자 트리거(Settings)에서 — bootSilent() 는 이미 허용된
 * 경우에만 토큰을 가져옴.
 *
 * native 진입점 (NativeMessaging) 은 src/services/FirebaseMessaging.ts 로
 * 분리되어 동적 import — 웹 번들에 capacitor-firebase/messaging 코드가
 * 포함되지 않음. RemoteConfig/AuthService/Telemetry 와 동일한 패턴.
 */

const isNative = (): boolean => Capacitor.isNativePlatform();

interface NativeMessagingAdapter {
  requestPermission(): Promise<boolean>;
  getToken(): Promise<string | null>;
  bootSilent(): Promise<string | null>;
}

let cached: Promise<NativeMessagingAdapter> | null = null;

function getNative(): Promise<NativeMessagingAdapter> {
  if (!cached) {
    cached = import('./FirebaseMessaging')
      .then((m) => {
        // 명시적 타입 assignment — NativeMessaging 시그니처 drift 시
        // 컴파일 타임에 실패.
        const inst: NativeMessagingAdapter = new m.NativeMessaging();
        return inst;
      })
      .catch((err) => {
        cached = null;
        throw err;
      });
  }
  return cached;
}

export async function requestPermission(): Promise<boolean> {
  if (!isNative()) return false;
  const n = await getNative();
  return n.requestPermission();
}

export async function getToken(): Promise<string | null> {
  if (!isNative()) return null;
  const n = await getNative();
  return n.getToken();
}

/** 부팅 후 호출 — 권한이 이미 허용돼 있으면 토큰만 가져옴, 아니면 SKIP.
 * 첫 권한 요청은 사용자 트리거(Settings)에서. */
export async function bootSilent(): Promise<string | null> {
  if (!isNative()) return null;
  try {
    const n = await getNative();
    return n.bootSilent();
  } catch {
    return null;
  }
}
